import { gql, useEnhancedQuery } from "@uplift-ltd/apollo";
import he from "he";
import React, { Fragment, useMemo } from "react";
import { PageIdType } from "__generated__/globalTypes";
import {
  STATIC_PAGE_QUERY,
  StaticPageQuery,
  StaticPageQueryVariables,
} from "components/StaticPageTemplate";
import Container from "components/common/Container";
import { FullPageLoading } from "components/common/Loading";
import SectionHeader from "components/common/SectionHeader";
import TableOfContents from "components/common/TableOfContents";
import { COURSE_CATALOG_VARIABLES } from "constants/wordpress";
import createAnchor from "helpers/createAnchor";
import { mapNodes } from "helpers/mapNodes";
import { useUserContext } from "hooks";
import { usePageMeta } from "hooks/usePageMeta";
import { AUTHOR_INFO_FRAGMENT } from "../fragments";
import Course from "./Course";
import CourseGroup from "./CourseGroup";
import {
  CourseCatalogEnrollmentQueryNode as CourseEnrollmentNode,
  CourseCatalogEnrollmentQueryQuery as CourseCatalogEnrollmentQuery,
  CourseCatalogGroupsEnrollmentQueryNode as CourseGroupEnrollmentNode,
  CourseCatalogGroupsEnrollmentQueryQuery as CourseCatalogGroupsEnrollmentQuery,
  CourseCatalogGroupsQueryNode as CourseGroupNode,
  CourseCatalogGroupsQueryQuery as CourseCatalogGroupsQuery,
  CourseCatalogQueryNode as CourseNode,
  CourseCatalogQueryQuery as CourseCatalogQuery,
} from "./__generated__/CourseCatalog";
import styles from "./CourseCatalog.module.css";

export type { CourseCatalogQuery, CourseCatalogGroupsQuery, CourseNode as Course };

export const COURSE_CATALOG_QUERY = gql`
  query CourseCatalogQuery($expiredCategories: [ID], $displayInCatalog: Boolean) {
    # TODO: consider how we'll query for 500 courses?
    courses(
      first: 1000
      where: {
        categoryNotIn: $expiredCategories
        orderby: { field: DATE, order: DESC }
        displayInCatalog: $displayInCatalog
      }
    ) {
      edges {
        node {
          id
          title
          ceUnits
          priceUSD
          subscriptionFeatureOptIns {
            membersExclusive
          }
          fulfillsRequirements {
            edges {
              node {
                id
                name
                description
                slug
              }
            }
          }
          courseFields {
            displayDateBadges
            courseMaterialsExtraText
          }
          materials
          slug
          date
          subjects
          isNew
          isRecentlyUpdated
          authors {
            id
            ...AuthorInfoFragment
          }
        }
      }
    }
  }
  ${AUTHOR_INFO_FRAGMENT}
`;

export const COURSE_CATALOG_ENROLLMENT_QUERY = gql`
  query CourseCatalogEnrollmentQuery($expiredCategories: [ID], $displayInCatalog: Boolean) {
    # TODO: consider how we'll query for 500 courses?
    courses(
      first: 1000
      where: {
        categoryNotIn: $expiredCategories
        orderby: { field: DATE, order: DESC }
        displayInCatalog: $displayInCatalog
      }
    ) {
      edges {
        node {
          id
          isEnrolled
        }
      }
    }
  }
`;

export const COURSE_CATALOG_GROUPS_QUERY = gql`
  query CourseCatalogGroupsQuery($expiredCategories: [ID], $displayInCatalog: Boolean) {
    courseGroups(
      first: 1000
      where: {
        categoryNotIn: $expiredCategories
        orderby: { field: DATE, order: DESC }
        displayInCatalog: $displayInCatalog
      }
    ) {
      edges {
        node {
          id
          title
          ceUnits
          priceUSD
          slug
          subjects
          hasCertificate
          courses {
            id
            title
          }
          subscriptionFeatureOptIns {
            membersExclusive
          }
        }
      }
    }
  }
`;

export const COURSE_CATALOG_GROUPS_ENROLLMENT_QUERY = gql`
  query CourseCatalogGroupsEnrollmentQuery($expiredCategories: [ID], $displayInCatalog: Boolean) {
    courseGroups(
      first: 1000
      where: {
        categoryNotIn: $expiredCategories
        orderby: { field: DATE, order: DESC }
        displayInCatalog: $displayInCatalog
      }
    ) {
      edges {
        node {
          id
          isEnrolled
        }
      }
    }
  }
`;

export const COURSE_CATALOG_PAGE_VARIABLES = {
  idType: PageIdType.URI,
  id: "course-catalog",
};

interface CourseCatalogProps {
  title: string;
}

const CourseCatalog: React.FC<CourseCatalogProps> = ({ title: initialTitle }) => {
  const { currentUser } = useUserContext();

  const { data: pageMetaData, loading: pageMetaLoading } = useEnhancedQuery<
    StaticPageQuery,
    StaticPageQueryVariables
  >(STATIC_PAGE_QUERY, {
    variables: COURSE_CATALOG_PAGE_VARIABLES,
  });

  const { data: courseCatalogData, loading: courseCatalogLoading } =
    useEnhancedQuery<CourseCatalogQuery>(COURSE_CATALOG_QUERY, {
      variables: COURSE_CATALOG_VARIABLES,
    });

  const { data: courseCatalogGroupsData, loading: courseCatalogGroupsLoading } =
    useEnhancedQuery<CourseCatalogGroupsQuery>(COURSE_CATALOG_GROUPS_QUERY, {
      variables: COURSE_CATALOG_VARIABLES,
    });

  const loading = pageMetaLoading || courseCatalogLoading || courseCatalogGroupsLoading;

  const { data: courseCatalogEnrollmentData } = useEnhancedQuery<CourseCatalogEnrollmentQuery>(
    COURSE_CATALOG_ENROLLMENT_QUERY,
    {
      variables: COURSE_CATALOG_VARIABLES,
      skip: !currentUser,
    }
  );

  const { data: courseCatalogGroupsEnrollmentData } =
    useEnhancedQuery<CourseCatalogGroupsEnrollmentQuery>(COURSE_CATALOG_GROUPS_ENROLLMENT_QUERY, {
      variables: COURSE_CATALOG_VARIABLES,
      skip: !currentUser,
    });

  const { seo, title: pageTitle } = pageMetaData?.page || {};
  const { summary, title: heroTitle } = pageMetaData?.page?.heroSection || {};

  usePageMeta(initialTitle || pageTitle, seo);

  const courses = mapNodes(courseCatalogData?.courses);
  const courseGroups = mapNodes(courseCatalogGroupsData?.courseGroups);
  const courseEnrollments = mapNodes(courseCatalogEnrollmentData?.courses);
  const courseGroupEnrollments = mapNodes(courseCatalogGroupsEnrollmentData?.courseGroups);

  const coursesAndGroupsBySubject = useMemo(() => {
    const coursesAndGroups = [...courses, ...courseGroups];
    const subjectDict: { [key: string]: (CourseNode | CourseGroupNode)[] } = {};
    // Iterate through courses
    coursesAndGroups.forEach(courseOrGroup =>
      // For every subject that they are related to store them in the subject dict
      courseOrGroup.subjects.forEach((subject: string) => {
        // Decode the html encoded subject string
        const translatedSubject = he.decode(subject);
        if (subjectDict[translatedSubject]) {
          // If the subject already exists in the dict then add it to the array
          subjectDict[translatedSubject].push(courseOrGroup);
          return;
        }
        // If not then start the array with the course
        subjectDict[translatedSubject] = [courseOrGroup];
      })
    );
    return subjectDict;
  }, [courses, courseGroups]);

  const enrollmentsByGlobalId = useMemo(() => {
    const enrollmentsDict: { [key: string]: boolean } = {};

    const courseAndGroupEnrollments: (CourseEnrollmentNode | CourseGroupEnrollmentNode)[] = [
      ...courseEnrollments,
      ...courseGroupEnrollments,
    ];
    courseAndGroupEnrollments.forEach(courseAndGroupEnrollment => {
      enrollmentsDict[courseAndGroupEnrollment.id] = courseAndGroupEnrollment.isEnrolled;
    });

    return enrollmentsDict;
  }, [courseEnrollments, courseGroupEnrollments]);

  const subjects = Object.keys(coursesAndGroupsBySubject)
    .sort((a: string, b: string) => {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    })
    .map(s => {
      const id = createAnchor(s);

      return {
        href: `#${id}`,
        id,
        label: s,
      };
    });

  if (loading) return <FullPageLoading delayMs={100} />;

  return (
    <>
      <SectionHeader title={heroTitle || initialTitle || pageTitle || ""} summary={summary || ""} />
      <Container>
        <div className={styles.courseCatalogMainWrapper}>
          <div className={styles.courseCatalogSubWrapper}>
            <TableOfContents items={subjects} />
            {subjects.map(subject => (
              <Fragment key={subject.label}>
                <h2 id={subject.id} className={styles.courseHeading}>
                  {subject.label}
                </h2>
                {coursesAndGroupsBySubject[subject.label]
                  .sort((a: CourseNode | CourseGroupNode, b: CourseNode | CourseGroupNode) => {
                    return (a.title?.toLowerCase() || "").localeCompare(
                      b.title?.toLowerCase() || ""
                    );
                  })
                  .map(courseOrGroup => (
                    <div key={courseOrGroup.id}>
                      {courseOrGroup.__typename === "Course" && (
                        <Course
                          {...courseOrGroup}
                          isEnrolled={enrollmentsByGlobalId[courseOrGroup.id]}
                          isMembersExclusive={
                            courseOrGroup.subscriptionFeatureOptIns?.membersExclusive || false
                          }
                        />
                      )}
                      {courseOrGroup.__typename === "CourseGroup" && (
                        <CourseGroup
                          {...courseOrGroup}
                          isEnrolled={enrollmentsByGlobalId[courseOrGroup.id]}
                          isMembersExclusive={
                            courseOrGroup.subscriptionFeatureOptIns?.membersExclusive || false
                          }
                        />
                      )}
                    </div>
                  ))}
              </Fragment>
            ))}
          </div>
        </div>
      </Container>
    </>
  );
};

export default CourseCatalog;
